import React, { useState } from "react";
import { toast } from "react-toastify";
import { useParams, useSearchParams } from "react-router-dom";
import { Button, Typography, Box } from "@mui/material";
import { Add, Edit } from "@mui/icons-material";

import FormDrawer from "components/Shared/FormDrawer";
import DriverLicenceForm from "screens/App/CRM/Driver/driverComponents/DriverLicenceForm";
import AppTable from "components/Shared/AppTable";
import FileIcon from "components/Shared/FileIcon";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import {
  useAddDriverLicenceMutation,
  useUpdateDriverLicenceMutation,
  useLazyListDriverLicenceQuery,
} from "store/services/driverApi";
import { useAuth } from "hooks";
import { useTablePagination } from "hooks";
import { BaseTabs } from "components/Shared/BaseTabs";

const DriverLicenceTabs = () => {
  const { id } = useParams();
  const { loggedInUser, isDriver } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [selectedLicence, setSelectedLicence] = useState(null);

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();

  const driverId = isDriver ? loggedInUser.typeRef : id;

  const licenceTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];
  const [fetchLicences, { data, isLoading, isFetching, error }] = useLazyListDriverLicenceQuery();

  const [addLicence] = useAddDriverLicenceMutation();
  const [updateLicence] = useUpdateDriverLicenceMutation();

  React.useEffect(() => {
    fetchLicences({
      driverId,
      page: currentPage,
      limit: perPage,
      active: activeTab === "Active",
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchLicences, keyword, perPage, driverId]);

  const handleSubmit = async (formData) => {
    try {
      if (selectedLicence) {
        await updateLicence({ driverId, licenceId: selectedLicence._id, ...formData }).unwrap();
        toast.success("Driver licence updated successfully");
      } else {
        await addLicence({ driverId, ...formData }).unwrap();
        toast.success("Driver licence added successfully");
      }
      setShowForm(false);
      setSelectedLicence(null);
    } catch (err) {
      toast.error("An error occurred");
    }
  };

  const columns = [
    {
      name: "Licence ID",
      selector: (row) => row.licenceIdd || "N/A",
      sortable: true
    },
    {
      name: "Licence Number",
      selector: (row) => row.licenceNumber || "N/A",
      sortable: true
    },
    {
      name: "Type",
      selector: (row) => row.type || "N/A",
      sortable: true
    },
    {
      name: "Status",
      selector: (row) => row.status || "N/A",
      sortable: true
    },
    {
      name: "Issue Date",
      selector: (row) => row.issueDate ? new Date(row.issueDate).toLocaleDateString() : "N/A",
      sortable: true
    },
    {
      name: "Expiry Date",
      selector: (row) => row.expiryDate ? new Date(row.expiryDate).toLocaleDateString() : "N/A",
      sortable: true
    },
    {
      name: "Attachments",
      cell: (row) => (
        <div className="flex gap-2">
          {row?.attachments?.length > 0 ? row.attachments.map((file) => (
            <a key={file._id} href={file.url} target="_blank" rel="noopener noreferrer">
              <FileIcon fileName={file.name} fileUrl={file.url} />
            </a>
          )) : "No attachments"}
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <Button onClick={() => { setSelectedLicence(row); setShowForm(true); }}>
          <Edit fontSize="small" />
        </Button>
      ),
    },
  ];

  const breadcrumbs = [{ title: "Driver Licences" }];

  return (
    <>
      {isDriver ? (
        <BreadcrumbLayout
          breadcrumbs={breadcrumbs}
          headerActions={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedLicence(null);
                setShowForm(true);
              }}
            >
              Add Licence
            </Button>
          }
        >
          <BreadcrumbLayout.Error error={error} />
          <BreadcrumbLayout.Paper>
            <BaseTabs
              tabs={licenceTabs}
              defaultValue={activeTab}
            />
            <AppTable
              columns={columns}
              data={data?.driverLicences || []}
              progressPending={isLoading || isFetching}
              paginationServer
              paginationPerPage={perPage}
              paginationTotalRows={data?.totalItems || 0}
              paginationDefaultPage={currentPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              noDataComponent={
                <Typography py={3} textAlign="center" color="textSecondary">
                  No licences found
                </Typography>
              }
            />
          </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column'}}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end',marginRight: "10px" }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedLicence(null);
                setShowForm(true);
              }}
            >
              Add Licence
            </Button>
          </Box>
          <BaseTabs tabs={licenceTabs} defaultValue={activeTab} />
          <AppTable
            columns={columns}
            data={data?.driverLicences || []}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={data?.totalItems || 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            noDataComponent={
              <Typography py={3} textAlign="center" color="textSecondary">
                No licences found
              </Typography>
            }
          />
        </Box>
      )}

      <FormDrawer
        open={showForm}
        onClose={() => setShowForm(false)}
        title={`${selectedLicence ? "Edit" : "Add"} Driver Licence`}
      >
        <DriverLicenceForm
          driverLicence={selectedLicence}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
          isSubmitting={false}
        />
      </FormDrawer>
    </>
  );
};

export default DriverLicenceTabs;